import { SheetLayer } from '@bluebeam/carnivore';
import ECSEditorContext from 'contexts/ECSEditorContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import Canvas from 'editor/components/Canvas';
import CarnivoreSheet from 'editor/components/CarnivoreSheet';
import CarnivoreSheetLayer from 'editor/components/CarnivoreSheetLayer';
import TileLayers from 'editor/components/TileLayers';
import TileSheet from 'editor/components/TileSheet';
import Zooming from 'editor/components/Zooming';
import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getTiledCanvas, scaleTileCanvas } from 'utils/image';

type Props = PropsWithChildren<{
  sheetComp: CarnivoreSheet;
  index: number;
  width: number;
  height: number;
  drawingtilescloudfronturl: string;
  drawingmaxzoom: number;
  drawingminzoom: number;
  drawingtilesize: number;
  onTilesLoaded: () => void;
}>;

// This component creates and manages the Carnivore "SheetLayer" component.
// it also inits the tiles for each zoom level and the ECS components which the systems uses to render them
const ECSTileSheetLayer: FC<Props> = (props: Props) => {
  const {
    sheetComp,
    index,
    width,
    height,
    drawingtilescloudfronturl,
    drawingmaxzoom,
    drawingminzoom,
    drawingtilesize,
    onTilesLoaded,
  } = props;
  const world = useContext(ECSWorldContext);
  const { doc, carnCtx } = useContext(ECSEditorContext);
  const [sheetLayer, setSheetLayer] = useState<SheetLayer | null>(null);
  const maxZoomLevel = drawingmaxzoom;
  const maxScale = 2 ** (drawingmaxzoom - 1);
  const minZoom = drawingminzoom;

  useEffect(() => {
    async function initTiles() {
      const _canvasComp = new Canvas();

      const firstZoomLevelCanvas = await scaleTileCanvas(
        await getTiledCanvas(
          drawingtilescloudfronturl,
          width,
          height,
          minZoom,
          drawingtilesize
        ),
        width * maxScale,
        height * maxScale,
        minZoom,
        maxZoomLevel
      );

      // Add entity for updating canvas on zoom and managing scale
      const scale = firstZoomLevelCanvas.width / width;

      world.createEntity().add(new TileSheet(firstZoomLevelCanvas, scale));

      // Used to indicate the TileSheet Component (ECS Component)
      // now exists in the World with the proper scale
      onTilesLoaded();

      // TBD - add keyboard entity. if so pass thru toolComp from EditorContext
      // world.createEntity().add(toolComp).add(new KeyboardState());

      const zoomMapCanvas = new Map([[1, firstZoomLevelCanvas]]);
      const _tileLayersComp = new TileLayers(zoomMapCanvas);

      _canvasComp.canvas = firstZoomLevelCanvas;

      const _sheetLayer = carnCtx.ctx.createSheetLayer({
        id: `sheet-layer-${index}`,
        index,
      });

      _sheetLayer.fillBuffer(_canvasComp.canvas);
      sheetComp.sheet.addSheetLayer(_sheetLayer.id);

      const _sheetLayerComp = new CarnivoreSheetLayer(_sheetLayer);
      world
        .createEntity()
        .add(doc)
        .add(new Zooming())
        .add(_tileLayersComp)
        .add(_sheetLayerComp);

      setSheetLayer(_sheetLayer);

      for (let i = minZoom + 1; i <= maxZoomLevel; i++) {
        getTiledCanvas(
          drawingtilescloudfronturl,
          width,
          height,
          i,
          drawingtilesize
        ).then((canvas) => {
          scaleTileCanvas(
            canvas,
            width * maxScale,
            height * maxScale,
            i,
            maxZoomLevel
          ).then((scaledCanvas) => {
            const tileLayer = world.grab(TileLayers);
            tileLayer?.component.zoomCanvases.set(i, scaledCanvas);
          });
        });
      }
    }

    initTiles();

    return () => {
      world
        .grabAll(TileSheet)
        .forEach(({ entity }) => entity.destroyImmediately());

      world
        .grabAll(TileLayers)
        .forEach(({ entity }): void => entity.destroyImmediately());
    };
  }, []);

  useEffect(() => {
    return () => {
      if (sheetLayer) {
        sheetLayer.destroy();
      }
    };
  }, [sheetLayer]);

  const { children } = props;
  return <>{children}</>;
};

export default ECSTileSheetLayer;
